/**
 * Factory for prescription API onSuccess/onError callbacks.
 * Reduces duplication across confirmation, pharmacy selection, send, and modification flows.
 */

import { replaceDosageWithDose } from "../textUtils";
import { createPrescriptionsFromMedicationData, checkPrescriptionSent } from "./prescriptionUtils";
import { ConversationItem } from "./types";

export interface PrescriptionCallbackOptions {
  lastConversation: ConversationItem;
  preferredPharmacy: string;
  /** When true, apply checkPrescriptionSent and replaceDosageWithDose to success result */
  checkSent?: boolean;
  /** Override prescriptions used when building success/error (e.g. updatedPrescriptions for pharmacy selection) */
  existingPrescriptions?: any[];
  /** Substitutions string for modification flow */
  substitutions?: string;
  /** Explicit pharmacy for createPrescriptionsFromMedicationData (e.g. pharmacy selection) */
  pharmacy?: string;
  /** Custom error fallback (e.g. modification: prescriptions with substitutions, different originalPrescription) */
  errorFallback?: () => {
    prescriptions: any[];
    aiResponse: string;
    originalPrescription?: any;
  };
}

export type PrescriptionAPICallbackResult = {
  prescriptions: any[];
  aiResponse: string;
  originalPrescription?: any;
};

/**
 * Creates onSuccess and onError callbacks for handlePrescriptionAPI options.
 * Use for: confirmed prescription, pharmacy selection, default pharmacy send, modified prescription.
 */
export function createPrescriptionAPICallbacks(
  options: PrescriptionCallbackOptions
): {
  onSuccess: (
    response: any,
    updatedStructuredData: any[],
    repromptMessage: string
  ) => PrescriptionAPICallbackResult;
  onError: () => PrescriptionAPICallbackResult;
} {
  const {
    lastConversation,
    preferredPharmacy,
    checkSent = false,
    existingPrescriptions,
    substitutions,
    pharmacy,
    errorFallback,
  } = options;

  const baseExisting = existingPrescriptions ?? lastConversation.prescriptions;
  const baseOriginal = lastConversation.originalPrescription;

  const onSuccess = (
    response: any,
    updatedStructuredData: any[],
    repromptMessage: string
  ): PrescriptionAPICallbackResult => {
    const prescriptions = createPrescriptionsFromMedicationData(
      updatedStructuredData,
      {
        aiResponse: repromptMessage || "",
        existingPrescriptions: baseExisting,
        defaultPharmacy: preferredPharmacy,
        ...(pharmacy !== undefined && { pharmacy }),
        ...(substitutions !== undefined && { substitutions }),
      }
    );

    if (!checkSent) {
      return {
        prescriptions,
        aiResponse: repromptMessage || "",
        originalPrescription: baseOriginal,
      };
    }

    const apiResponse = replaceDosageWithDose(response?.data?.response || "");
    const aiResponse = apiResponse || repromptMessage || "";
    // Mark sent rather than discarding: wiping to [] here used to also erase the
    // medication name/identity needed for updateHeldConversations' retroactive
    // "sent" back-fill onto earlier cards (see conversationUtils.ts) — this turn's
    // own card is still hidden the same as before via the backend's ui_blocks/
    // current_state gating in AIResponseBox, which doesn't depend on this array
    // being empty.
    const finalPrescriptions =
      apiResponse && checkPrescriptionSent(apiResponse)
        ? prescriptions.map((p) => ({ ...p, sent: true }))
        : prescriptions;

    return {
      prescriptions: finalPrescriptions,
      aiResponse,
      originalPrescription: baseOriginal,
    };
  };

  const onError = (): PrescriptionAPICallbackResult => {
    if (errorFallback) {
      return errorFallback();
    }
    return {
      prescriptions: baseExisting,
      aiResponse: "",
      originalPrescription: baseOriginal,
    };
  };

  return { onSuccess, onError };
}

/**
 * Creates callbacks for the modification flow (substitutions change).
 * onError returns prescriptions with substitutions applied and a fallback originalPrescription.
 */
export function createModificationCallbacks(
  options: PrescriptionCallbackOptions & { modifiedSubstitutions: string }
): {
  onSuccess: (
    response: any,
    updatedStructuredData: any[],
    repromptMessage: string
  ) => PrescriptionAPICallbackResult;
  onError: () => PrescriptionAPICallbackResult;
} {
  const { lastConversation, modifiedSubstitutions, ...rest } = options;
  return createPrescriptionAPICallbacks({
    ...rest,
    lastConversation,
    substitutions: modifiedSubstitutions,
    errorFallback: () => ({
      prescriptions: lastConversation.prescriptions.map((p) => ({
        ...p,
        substitutions: modifiedSubstitutions,
      })),
      aiResponse: "",
      originalPrescription:
        lastConversation.originalPrescription || lastConversation.prescriptions[0],
    }),
  });
}
